import { useCallback, useRef, useState } from 'react';
import { openStripeExpress, StripeExpressOpenFailure } from 'utils/stripeExpressLink';

/**
 * T2-7437: open Stripe Express from a button, once per tap. A second tap while
 * the link is loading does nothing; a failure is kept for the screen to show.
 * `canOpen` lets a screen stop the open once the link arrives (see
 * openStripeExpress); a stopped open shows no failure.
 */
export default function useStripeExpressLink() {
  const openingRef = useRef(false);
  const [opening, setOpening] = useState(false);
  const [failure, setFailure] = useState<StripeExpressOpenFailure | null>(null);

  const open = useCallback(async (canOpen?: () => boolean): Promise<boolean> => {
    if (openingRef.current) return false;
    openingRef.current = true;
    setOpening(true);
    setFailure(null);
    try {
      const result = await openStripeExpress(canOpen);
      if (!result.opened && !('cancelled' in result)) setFailure(result);
      return result.opened;
    } finally {
      openingRef.current = false;
      setOpening(false);
    }
  }, []);

  const clearFailure = useCallback(() => setFailure(null), []);

  return { opening, failure, open, clearFailure };
}
